/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {
    useAccessToken,
    useCommerceApi,
    useCustomerBaskets,
    useShopperBasketsMutation
} from '@salesforce/commerce-sdk-react'
import {StorefrontPreview} from '@salesforce/commerce-sdk-react/components'
import * as queryKeyHelpers from '@salesforce/commerce-sdk-react/hooks/ShopperProducts/queryKeyHelpers'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'
import {useQueries, useQuery} from '@tanstack/react-query'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'

// Chakra
import {SkipNavContent, SkipNavLink} from '@chakra-ui/skip-nav'
import {
    Box,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Contexts
import {CurrencyProvider} from '@salesforce/retail-react-app/app/contexts'

// Local Project Components

import OfflineBanner from '@salesforce/retail-react-app/app/components/offline-banner'
import OfflineBoundary from '@salesforce/retail-react-app/app/components/offline-boundary'
import ScrollToTop from '@salesforce/retail-react-app/app/components/scroll-to-top'

// Hooks
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {AuthModal, useAuthModal} from '../../hooks/use-auth-modal'
import {useHashFragment} from '../../hooks/use-hash-fragment'

// Localization
import {IntlProvider} from 'react-intl'

// Others
import {ACTIVE_DATA_ENABLED} from '@salesforce/retail-react-app/app/constants'
import {
    fetchTranslations,
    getTargetLocale
} from '@salesforce/retail-react-app/app/utils/locale'
import {
    isServer,
    watchOnlineStatus
} from '@salesforce/retail-react-app/app/utils/utils'

import {Helmet} from 'react-helmet'
import {messagesQuery} from '../../clients/contentful/queries'
import {gqlFetchContentful} from '../../config'
import {
    DEFAULT_LOCALE,
    DEFAULT_SITE_TITLE,
    languageAliases
} from '../../constants'
import {
    getGoogleTagManagerContainerId,
    getParamsFromPath
} from '../../utils/site-utils'
import {getPathWithLocale, getUrlWithLocale} from '../../utils/url'
import FooterWrapper from '../footer/footer-wrapper'
import {HeaderWrapper} from '../header/header-wrapper'
import Seo from '../seo'
import ChordInitializer from '../../chord-analytics'
import Customs from './customs'

const onClient = typeof window !== 'undefined'
const App = (props) => {
    const {children} = props
    const {
        app: {commerceAPI},
        contentfulEnvironment,
        rennaiRootUrl
    } = getConfig()
    const {getTokenWhenReady} = useAccessToken()
    const appOrigin = getAppOrigin()
    const activeData = useActiveData()
    const currentUrl = onClient && window.location.pathname

    const location = useLocation()
    const authModal = useAuthModal()
    const {site, locale, buildUrl} = useMultiSite()

    const [isOnline, setIsOnline] = useState(true)
    const styles = useStyleConfig('App')

    const [urlBase, setUrlBase] = useState(
        rennaiRootUrl || 'https://www.rennai.com'
    )

    const isDev = !['prod', 'master'].includes(contentfulEnvironment)

    const {
        parameters: {mid}
    } = commerceAPI

    const targetLocale = getTargetLocale({
        getUserPreferredLocales: () => {
            const userLocale = onClient && localStorage.getItem('locale')
            const userLocaleAlias = site?.l10n.supportedLocales.find(
                (locale) => locale.id === userLocale
            )?.alias

            console.log(
                '🚀 ~ file: index.jsx:105 ~ App ~ currentUrl:',
                currentUrl,
                userLocale,
                locale.id,
                locale.id !== userLocale,
                userLocaleAlias
            )

            const path = `${location.pathname}${location.search}`

            const {siteRef} = getParamsFromPath(path)

            if (siteRef) {
                return
            } else if (
                userLocale &&
                locale.id !== userLocale &&
                !currentUrl?.includes(userLocaleAlias)
            ) {
                const newUrl = getPathWithLocale(userLocale, buildUrl, {
                    disallowParams: ['refine']
                })

                window.location = newUrl
            }

            // CONFIG: This function should return an array of preferred locales. They can be
            // derived from various sources. Below are some examples of those:
            //
            // - client side: window.navigator.languages
            // - the page URL they're on (example.com/en-GB/home)
            // - cookie (if their previous preference is saved there)
            //
            // If this function returns an empty array (e.g. there isn't locale in the page url),
            // then the app would use the default locale as the fallback.

            // NOTE: Your implementation may differ, this is just what we did.
            return [userLocale || locale?.id || DEFAULT_LOCALE]
        },
        l10nConfig: site.l10n
    })

    // If the translation file exists, it'll be served directly from static folder (and won't reach this code here).
    // However, if the file is missing, the App would render a 404 page.
    const is404ForMissingTranslationFile =
        /\/static\/translations\/compiled\/[^.]+\.json$/.test(
            location?.pathname
        )

    // Fetch the translation message data using the target locale.
    const {data: messages} = useQuery({
        queryKey: ['app', 'translations', 'messages', targetLocale],
        queryFn: async () => {
            if (is404ForMissingTranslationFile) {
                // Return early to prevent an infinite loop
                // Otherwise, it'll continue to fetch the missing translation file again
                return {}
            }
            const fileMessages = await fetchTranslations(targetLocale)
            const contentFulMessages = await gqlFetchContentful(messagesQuery, {
                locale: targetLocale
            })

            const convertedMessages =
                contentFulMessages?.localizedMessagesCollection?.items?.reduce(
                    (acc, message) => {
                        acc[message.id] = message.value
                        return acc
                    },
                    {}
                )

            const messages = {...fileMessages, ...convertedMessages}

            return messages
        },
        enabled: isServer
    })

    // Used to conditionally render header/footer for checkout page
    const isCheckout = /\/checkout$/.test(location?.pathname)

    const {l10n} = site
    // Get the current currency to be used through out the app
    const currency = locale.preferredCurrency || l10n.defaultCurrency

    // Handle creating a new basket if there isn't one already assigned to the current
    // customer.
    const {data: customer} = useCurrentCustomer()
    const {data: baskets} = useCustomerBaskets(
        {parameters: {customerId: customer.customerId}},
        {enabled: !!customer.customerId && !isServer}
    )
    const {data: basket} = useCurrentBasket()

    const createBasket = useShopperBasketsMutation('createBasket')
    const updateBasket = useShopperBasketsMutation('updateBasket')
    const updateCustomerForBasket = useShopperBasketsMutation(
        'updateCustomerForBasket'
    )

    const getHrefLangUrl = useCallback(
        (locale) => {
            const _url = urlBase?.endsWith('/') ? urlBase.slice(0, -1) : urlBase
            const url = `${_url}${getUrlWithLocale(
                locale.alias,
                site.l10n.defaultLocale,
                {location}
            )}`?.split('?')?.[0]
            return url.replaceAll(_url, '') === '/en' ? _url : url
        },
        [appOrigin, location]
    )

    const {
        canonical,
        xDefault,
        locales = [],
        aliasLocales = []
    } = useMemo(() => {
        const isEnglish = locale.id === languageAliases.en.locale
        const current = isEnglish ? languageAliases.en : languageAliases.fr

        return {
            canonical: getHrefLangUrl({alias: current.localeName}),
            xDefault: getHrefLangUrl({alias: languageAliases.en.localeName}),
            locales: (site.l10n?.supportedLocales ?? []).map((locale) => ({
                rel: 'alternate',
                hrefLang: locale.id,
                href: getHrefLangUrl(locale),
                key: locale.id
            })),
            aliasLocales: (site.l10n?.supportedLocales ?? []).map((locale) => ({
                rel: 'alternate',
                hrefLang: locale.alias,
                href: getHrefLangUrl(locale),
                key: locale.id
            }))
        }
    }, [appOrigin, location])

    useEffect(() => {
        // Create a new basket if the current customer doesn't have one.
        if (baskets?.total === 0) {
            createBasket.mutate({
                body: {}
            })
        }
    }, [baskets])

    useEffect(() => {
        // update the basket currency if it doesn't match the current locale currency
        if (basket?.currency && basket?.currency !== currency) {
            updateBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {currency}
            })
        }
    }, [basket?.currency])

    useEffect(() => {
        // update the basket customer email
        if (
            basket &&
            customer?.isRegistered &&
            customer?.email &&
            customer?.email !== basket?.customerInfo?.email
        ) {
            updateCustomerForBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {
                    email: customer.email
                }
            })
        }
    }, [customer?.isRegistered, customer?.email, basket?.customerInfo?.email])

    useEffect(() => {
        // Listen for online status changes.
        watchOnlineStatus((isOnline) => {
            setIsOnline(isOnline)
        })
    }, [])

    useEffect(() => {
        setUrlBase(
            window?.location?.origin ||
                rennaiRootUrl ||
                'https://www.rennai.com'
        )
    }, [currentUrl])

    const trackPage = () => {
        activeData.trackPage(site.id, locale.id, currency)
    }

    useEffect(() => {
        trackPage()
    }, [location])

    useHashFragment()

    return (
        <IntlProvider
            onError={(err) => {
                if (!messages) {
                    // During the ssr prepass phase the messages object has not loaded, so we can suppress
                    // errors during this time.
                    return
                }
                if (err.code === 'MISSING_TRANSLATION') {
                    // NOTE: Remove the console error for missing translations during development,
                    // as we knew translations would be added later.
                    console.warn('Missing translation', err.message)
                    return
                }
                throw err
            }}
            locale={targetLocale}
            messages={messages}
            // For react-intl, the _default locale_ refers to the locale that the inline `defaultMessage`s are written for.
            // NOTE: if you update this value, please also update the following npm scripts in `template-retail-react-app/package.json`:
            // - "extract-default-translations"
            // - "compile-translations:pseudo"
            defaultLocale={DEFAULT_LOCALE}
        >
            <Customs isDev={isDev}>
                <Box
                    className="sf-app"
                    {...styles.container}
                    sx={{'-webkit-tap-highlight-color': 'transparent'}}
                >
                    <StorefrontPreview getToken={getTokenWhenReady}>
                        <Helmet
                            script={[
                                {
                                    type: 'text/javascript',
                                    innerHTML: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                                               new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                                               j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                                               'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                                               })(window,document,'script','dataLayer', "${getGoogleTagManagerContainerId()}");`
                                },
                                // hidden script for canada post
                                // {
                                //     type: 'text/javascript',
                                //     src: 'https://ws1.postescanada-canadapost.ca/js/addresscomplete-2.50.min.js?key=kw36-eg86-pe58-be41'
                                // },
                                ACTIVE_DATA_ENABLED
                                    ? {
                                          type: 'text/javascript',
                                          id: 'headActiveData',
                                          src: getAssetUrl(
                                              'static/head-active_data.js'
                                          )
                                      }
                                    : {}
                            ]}
                            link={[
                                {
                                    rel: 'stylesheet',
                                    type: 'text/css',
                                    href: 'https://ws1.postescanada-canadapost.ca/css/addresscomplete-2.50.min.css?key=kw36-eg86-pe58-be41'
                                }
                            ]}
                        ></Helmet>
                        {messages && (
                            <CurrencyProvider currency={currency}>
                                <Seo noIndex={isDev}>
                                    {/* Urls for all localized versions of this page (including current page)
                                    For more details on hrefLang, see https://developers.google.com/search/docs/advanced/crawling/localized-versions */}
                                    <link rel="canonical" href={canonical} />
                                    <link
                                        rel="alternate"
                                        hrefLang="x-default"
                                        href={xDefault}
                                    />
                                    {[...aliasLocales, ...locales]?.map(
                                        (attrs) => (
                                            <link key={attrs.key} {...attrs} />
                                        )
                                    )}
                                    <meta name="theme-color" content={'#fff'} />
                                    <meta
                                        name="apple-mobile-web-app-title"
                                        content={DEFAULT_SITE_TITLE}
                                    />
                                    <link
                                        rel="apple-touch-icon"
                                        href={getAssetUrl(
                                            'static/img/global/apple-touch-icon.png'
                                        )}
                                    />
                                    <link
                                        rel="manifest"
                                        href={getAssetUrl(
                                            'static/manifest.json'
                                        )}
                                    />
                                    <script
                                        src={`https://${mid}.collect.igodigital.com/collect.js`}
                                    />
                                    {/* A general locale as fallback. For example: "en" if default locale is "en-GB" */}
                                    {/* A wider fallback for user locales that the app does not support */}
                                    <script
                                        src="https://www.booxi.com/booknow/booknow.js"
                                        async=""
                                    ></script>
                                    {/* <script
                                        async
                                        src="https://www.googletagmanager.com/gtag/js?id=G-3RFZ17WZ6K"
                                    ></script> */}
                                    {/* <script>
                                        const onClient = typeof window !== 'undefined'
                                        if(onClient) {
                                            window.dataLayer = window.dataLayer || [];
                                            function gtag()
                                            {window.dataLayer.push(arguments)} b
                                            gtag('js', new Date()); gtag('config',
                                            {googleAnalyticsApiKey});
                                        }
                                    </script> */}
                                </Seo>
                                <ScrollToTop />

                                <Box
                                    id="app"
                                    display="flex"
                                    flexDirection="column"
                                    flex={1}
                                >
                                    <SkipNavLink zIndex="skipLink">
                                        Skip to Content
                                    </SkipNavLink>

                                    <HeaderWrapper
                                        styles={styles}
                                        isCheckout={isCheckout}
                                    />

                                    {!isOnline && <OfflineBanner />}
                                    <SkipNavContent
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            flex: 1,
                                            outline: 0
                                        }}
                                    >
                                        <Box
                                            as="main"
                                            id="app-main"
                                            role="main"
                                            display="flex"
                                            flexDirection="column"
                                            flex="1"
                                        >
                                            <OfflineBoundary isOnline={false}>
                                                {children}
                                            </OfflineBoundary>
                                        </Box>
                                    </SkipNavContent>

                                    <FooterWrapper />

                                    <AuthModal {...authModal} />
                                </Box>
                            </CurrencyProvider>
                        )}
                        {ACTIVE_DATA_ENABLED && (
                            <script
                                type="text/javascript"
                                src={getAssetUrl('static/dwanalytics-22.2.js')}
                                id="dwanalytics"
                                async="async"
                                onLoad={trackPage}
                            ></script>
                        )}
                        {ACTIVE_DATA_ENABLED && (
                            <script
                                src={getAssetUrl('static/dwac-21.7.js')}
                                type="text/javascript"
                                id="dwac"
                                async="async"
                            ></script>
                        )}
                        {<ChordInitializer />}
                    </StorefrontPreview>
                </Box>
            </Customs>
        </IntlProvider>
    )
}

App.propTypes = {
    children: PropTypes.node
}

/**
 * a hook that parallelly and individually fetches category based on the given ids
 * @param ids - list of categories ids to fetch
 * @param queryOptions -  react query options
 * @return list of react query results
 */
export const useCategoryBulk = (ids = [], queryOptions) => {
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()
    const {
        app: {commerceAPI}
    } = getConfig()
    const {
        parameters: {organizationId}
    } = commerceAPI
    const {site} = useMultiSite()

    const queries = ids.map((id) => {
        return {
            queryKey: queryKeyHelpers.getCategory.queryKey({
                id,
                levels: 2,
                organizationId,
                siteId: site.id
            }),
            queryFn: async () => {
                const token = await getTokenWhenReady()
                const res = await api.shopperProducts.getCategory({
                    parameters: {id, levels: 2},
                    headers: {
                        authorization: `Bearer ${token}`
                    }
                })
                return res
            },
            ...queryOptions,
            enabled: queryOptions.enabled !== false && Boolean(id)
        }
    })
    const res = useQueries({queries})
    return res
}

export default App
